// @ts-strict-ignore
import React, { useEffect, useMemo, useState } from 'react'
import { IonGrid, IonRow } from '@ionic/react'

import { SectionMediaVideoContainer as Container } from 'mytours-read-common'

import AudioTranscript from '../../../audio/components/AudioTranscript'

import Button from '../../../common/components/Button'
import Modal from '../../../common/components/modal'
import strings from '../../../../strings'

import '../styles/sectionMediaVideo.less'
import { ImageSizePreset } from '../../../../common_lib/types'
import { generateImageUrl } from '../../../../common_lib/utils'
import { getAllSounds } from '../../../audio/soundController'
import { formatNumberStyle } from '../../../platform'
import { ionHistory } from 'mobile_web/modules/navigation'
import { updateTranscriptionHash } from '../../functions'
import classNames from 'classnames'
import FullscreenVideoPlayer from './FullScreenVideo'

interface OwnProps {
	// if single item we pass in the width to use, else we pass in the height and width is based on keeping aspect ratio the same
	width?: number
	height?: number
	onPlay?: () => void
	isSingleItem?: boolean
	sectionId: number
	sectionTitle: string
	containerName: string
}

type Props = OwnProps & Container.Props & Container.Actions

const SectionMediaVideo: React.FC<Props> = props => {
	const [showTranscriptModal, setShowTranscriptModal] = useState<boolean>(false)
	const [showFullscreenVideoModal, setShowFullscreenVideoModal] = useState<boolean>(false)
	const [isFullScreenModal, setFullScreenModal] = useState<boolean>(false)

	const [hash, setHash] = useState<string>('')
	const [unListen, setUnListen] = useState<() => void>(undefined)

	useEffect(() => {
		const newUnListen = ionHistory.listen(({ hash: newHash }) => {
			if (newHash !== hash) {
				// TODO: this is temp fix: add global handle for hash changes
				if (newHash === '' && hash === '#transcript' && showTranscriptModal) {
					toggleTranscriptModal(false)
				}

				setHash(newHash)
			}
		})

		setUnListen(newUnListen)

		return () => {
			unListen?.()
		}
	}, [])

	const onPlayPausePress = (event: React.MouseEvent<HTMLIonButtonElement>): void => {
		event.stopPropagation()

		try {
			const currentSound = getAllSounds()
			if (currentSound) {
				const sounds = getAllSounds()
				for (const sound of sounds) {
					if (sound.playing()) sound.pause()
				}
			}
		} catch (error) {
			console.error('create sound error', error)
		}
		if (props.onPlay) props.onPlay()
		else toggleFullscreenVideoModal()
	}

	const toggleTranscriptModal = (value?: boolean): void => {
		updateTranscriptionHash(value, showTranscriptModal, () => {
			// TODO: add `video_view_transcript` event
		})

		setShowTranscriptModal(typeof value === 'boolean' ? value : !showTranscriptModal)
	}

	const renderTranscriptModal = useMemo(
		(): JSX.Element => (
			<Modal isOpen={showTranscriptModal}>
				<AudioTranscript transcript={props.sectionItem.transcription} onBack={toggleTranscriptModal} />
			</Modal>
		),
		[props.sectionItem.transcription],
	)

	const toggleFullscreenVideoModal = (): void => {
		setShowFullscreenVideoModal(!showFullscreenVideoModal)
	}

	const renderFullscreenVideoModal = useMemo(
		() => (
			<Modal
				isOpen={showFullscreenVideoModal}
				cssClass={classNames('media-modal', {
					'fullscreen-modal': isFullScreenModal,
				})}
				onDidDismiss={() => setShowFullscreenVideoModal(false)}
			>
				<FullscreenVideoPlayer
					sectionItem={props.sectionItem}
					sectionId={props.sectionId}
					sectionTitle={props.sectionTitle}
					containerName={props.containerName}
					onBack={toggleFullscreenVideoModal}
					setFullScreenModal={setFullScreenModal}
					isFullScreenModal={isFullScreenModal}
					isSingleItem={props.isSingleItem}
					isOpen={showFullscreenVideoModal}
				/>
			</Modal>
		),
		[props.sectionItem, props.isSingleItem, showFullscreenVideoModal, isFullScreenModal],
	)

	const imageUrl = useMemo<string | undefined>(() => {
		const {
			sectionItem: { thumbnail_media_item: thumbnailMediaItem },
		} = props

		const url = thumbnailMediaItem?.file
			? generateImageUrl(thumbnailMediaItem?.file, {
				imageSizePreset: ImageSizePreset.Row,
			  })
			: undefined

		return url
	}, [props.sectionItem])

	const onTranscriptPressed = (event: React.MouseEvent<HTMLIonButtonElement>) => {
		event.stopPropagation()

		if (props.sectionItem.transcription) {
			toggleTranscriptModal(true)
		}
	}

	const {
		sectionItem: { transcription },
		width: sectionWidth,
		height: sectionHeight,
	} = props

	const height = sectionHeight || (sectionWidth ? (sectionWidth / 3) * 2 : undefined)
	const width = sectionWidth || (height ? (height / 2) * 3 : undefined)

	return (
		<IonGrid className="video-item">
			<IonRow
				className="cover-image"
				style={{
					backgroundImage: imageUrl ? `url(${imageUrl})` : undefined,
					height: formatNumberStyle(height),
					width: formatNumberStyle(width),
				}}
			>
				<Button
					className="play-button"
					ariaLabel={strings.play_media}
					onClick={onPlayPausePress}
					data-cy="section-media-video-button"
				>
					<i className="fas fa-play" />
				</Button>
			</IonRow>

			{!!transcription && (
				<IonRow className="transcript-row">
					<Button fill="clear" ariaLabel={strings.transcript} onClick={onTranscriptPressed} className="transcript">
						<i className="fas fa-align-left" />
					</Button>
				</IonRow>
			)}
			{renderTranscriptModal}
			{renderFullscreenVideoModal}
		</IonGrid>
	)
}

const SectionMediaVideoComponent = Container.default(SectionMediaVideo)
export default SectionMediaVideoComponent as any
